import { PyodideState } from '../services/PyodideService';
import { SidebarTab } from '../../types';
import { dbStorage } from '../utils/dbStorage';
import { makeAutoObservable } from 'mobx';
import { pyodideService } from '../services/PyodideService';

// Define loading stages for the editor
export enum EditorLoadingStage {
    INITIAL = "Initial page load",
    LOADING_PROJECT = "Loading project data",
    LOADING_FILESYSTEM = "Loading project files",
    RENDERING_UI = "Preparing editor",
    INITIALIZING_PYODIDE = "Initializing Python environment",
    READY = "Ready"
}

// Define view modes for the editor
export enum EditorViewMode {
    CODE = "code",
    PREVIEW = "preview",
    SPLIT = "split"
}

interface CodeVersion {
    content: string;
    timestamp: number;
    id: string;
}

class UIStore {
    activeTab: SidebarTab = SidebarTab.Editor;
    logsVisible: boolean = false;
    agentVisible: boolean = true;
    isCollapsed: boolean = false;
    logs: string[] = [];
    pyodideTimedOut: boolean = false;
    viewMode: EditorViewMode = EditorViewMode.SPLIT;  // Default to split view
    initialAgentPrompt: string | null = null;  // Store the initial prompt for the agent panel

    // New properties for loading states
    loadingStage: EditorLoadingStage = EditorLoadingStage.INITIAL;
    pyodideLoadingStatus: string = "";
    filesystemReady: boolean = false;
    shouldShowLoadingScreen: boolean = true;

    versions: CodeVersion[] = [];
    currentVersionIndex: number = -1;
    showVersionBanner: boolean = false;
    lastAutoSaveContent: string = "";
    versionCheckInterval: NodeJS.Timeout | null = null;
    hasUnsavedChangesInPreviousVersion: boolean = false;
    pendingVersionContent: string = "";

    currentProjectId: string | null = null;

    constructor() {
        makeAutoObservable(this);
    }

    setActiveTab(tab: SidebarTab) {
        this.activeTab = tab;
    }

    setLogsVisible(visible: boolean) {
        this.logsVisible = visible;
    }

    setAgentVisible(visible: boolean) {
        this.agentVisible = visible;
    }

    setIsCollapsed(collapsed: boolean) {
        this.isCollapsed = collapsed;
    }

    updateLogs(message: string) {
        this.logs = [...this.logs, message];
    }

    handleToggleSidebar() {
        this.isCollapsed = !this.isCollapsed;
    }

    setPyodideTimedOut(timedOut: boolean) {
        this.pyodideTimedOut = timedOut;
    }

    setViewMode(mode: EditorViewMode) {
        this.viewMode = mode;
    }

    // Loading stage management
    setLoadingStage(stage: EditorLoadingStage) {
        this.loadingStage = stage;

        // Automatically set shouldShowLoadingScreen based on the stage
        // Updated to not show loading screen once filesystem is ready or rendering UI
        this.shouldShowLoadingScreen = (
            stage !== EditorLoadingStage.READY &&
            stage !== EditorLoadingStage.RENDERING_UI &&
            !(this.filesystemReady && stage === EditorLoadingStage.INITIALIZING_PYODIDE)
        );
    }

    setPyodideLoadingStatus(status: string) {
        this.pyodideLoadingStatus = status;

        // Hide loading screen if filesystem is ready, even if Pyodide is still initializing
        if (this.filesystemReady && this.shouldShowLoadingScreen) {
            this.shouldShowLoadingScreen = false;
        }
    }

    setFilesystemReady(ready: boolean) {
        this.filesystemReady = ready;

        // If filesystem is ready, we can hide the loading screen and move to rendering UI
        if (ready) {
            this.shouldShowLoadingScreen = false;

            // If we're still in the filesystem loading stage, move to rendering UI
            if (this.loadingStage === EditorLoadingStage.LOADING_FILESYSTEM) {
                this.setLoadingStage(EditorLoadingStage.RENDERING_UI);
            }
        }
    }

    // Helper method to get appropriate loading message
    getLoadingMessage(): string {
        switch (this.loadingStage) {
            case EditorLoadingStage.INITIAL:
                return "Starting editor...";
            case EditorLoadingStage.LOADING_PROJECT:
                return "Loading project data...";
            case EditorLoadingStage.LOADING_FILESYSTEM:
                return "Loading project files from storage...";
            case EditorLoadingStage.RENDERING_UI:
                return "Preparing editor interface...";
            case EditorLoadingStage.INITIALIZING_PYODIDE:
                if (this.pyodideLoadingStatus === 'loading-pyodide') return "Initializing Python environment...";
                if (this.pyodideLoadingStatus === 'setting-up-environment') return "Setting up environment...";
                if (this.pyodideLoadingStatus === 'installing-packages') return "Installing required packages...";
                if (this.pyodideLoadingStatus === 'loading-files') return "Loading project files...";
                if (this.pyodideLoadingStatus === 'running-script') return "Running initial script...";
                if (this.pyodideLoadingStatus === 'clearing-filesystem') return "Preparing filesystem...";
                return "Initializing Python environment...";
            default:
                return "Loading...";
        }
    }

    // Helper method to initialize Pyodide
    async initializePyodide() {
        if (!pyodideService.isReady()) {
            // Don't show loading screen if filesystem is ready
            const prevLoadingStage = this.loadingStage;
            this.setLoadingStage(EditorLoadingStage.INITIALIZING_PYODIDE);

            // Initialize Pyodide without blocking the UI if filesystem is ready
            if (this.filesystemReady && this.shouldShowLoadingScreen) {
                this.shouldShowLoadingScreen = false;
            }

            this.updateLogs("Initializing Pyodide Web Worker...");
            try {
                await pyodideService.initialize();
                this.setLoadingStage(EditorLoadingStage.READY);
            } catch (e) {
                if (e instanceof Error) {
                    this.updateLogs(`Error initializing Pyodide: ${e.message}`);
                }

                // If there was an error but filesystem is ready, we can still show the UI
                if (this.filesystemReady) {
                    this.shouldShowLoadingScreen = false;
                    this.setLoadingStage(EditorLoadingStage.RENDERING_UI);
                }
            }
        }
    }

    async initVersioning(initialContent: string, projectId?: string) {
        if (projectId) {
            this.currentProjectId = projectId;
            const savedContent = await dbStorage.getHelloPyContent(projectId);
            const contentToUse = savedContent || initialContent;

            const version = {
                content: contentToUse,
                timestamp: Date.now(),
                id: `v-${Date.now()}`
            };
            this.versions = [version];
            this.currentVersionIndex = 0;
            this.lastAutoSaveContent = contentToUse;
            this.showVersionBanner = false;

            if (savedContent && savedContent !== initialContent) {
                return contentToUse;
            }
        } else {
            const version = {
                content: initialContent,
                timestamp: Date.now(),
                id: `v-${Date.now()}`
            };
            this.versions = [version];
            this.currentVersionIndex = 0;
            this.lastAutoSaveContent = initialContent;
            this.showVersionBanner = false;
        }

        return null;
    }

    async addVersion(content: string) {
        if (content === this.lastAutoSaveContent) return;

        const version = {
            content,
            timestamp: Date.now(),
            id: `v-${Date.now()}`
        };

        if (this.currentVersionIndex < this.versions.length - 1) {
            this.versions = this.versions.slice(0, this.currentVersionIndex + 1);
        }

        this.versions.push(version);
        this.currentVersionIndex = this.versions.length - 1;
        this.lastAutoSaveContent = content;
        this.showVersionBanner = false;
        this.hasUnsavedChangesInPreviousVersion = false;
        this.pendingVersionContent = "";

        if (this.currentProjectId) {
            await dbStorage.storeHelloPyContent(this.currentProjectId, content);
        }
    }

    canUndo(): boolean {
        return this.currentVersionIndex > 0;
    }

    canRedo(): boolean {
        return this.currentVersionIndex < this.versions.length - 1;
    }

    undo(): string | null {
        if (!this.canUndo()) return null;
        this.currentVersionIndex--;
        this.showVersionBanner = this.currentVersionIndex < this.versions.length - 1;
        this.hasUnsavedChangesInPreviousVersion = false;
        this.pendingVersionContent = "";
        return this.versions[this.currentVersionIndex].content;
    }

    redo(): string | null {
        if (!this.canRedo()) return null;
        this.currentVersionIndex++;
        this.showVersionBanner = this.currentVersionIndex < this.versions.length - 1;
        this.hasUnsavedChangesInPreviousVersion = false;
        this.pendingVersionContent = "";
        return this.versions[this.currentVersionIndex].content;
    }

    getCurrentVersion(): CodeVersion | null {
        if (this.currentVersionIndex >= 0 && this.currentVersionIndex < this.versions.length) {
            return this.versions[this.currentVersionIndex];
        }
        return null;
    }

    getLatestVersion(): CodeVersion | null {
        return this.versions.length > 0 ? this.versions[this.versions.length - 1] : null;
    }

    async restoreCurrentVersion(): Promise<string | null> {
        if (this.currentVersionIndex >= 0 && this.currentVersionIndex < this.versions.length) {
            const content = this.versions[this.currentVersionIndex].content;
            this.currentVersionIndex = this.versions.length - 1;
            await this.addVersion(content);
            this.showVersionBanner = false;
            this.hasUnsavedChangesInPreviousVersion = false;
            this.pendingVersionContent = "";
            return content;
        }
        return null;
    }

    async goToLatestVersion(): Promise<string | null> {
        if (this.versions.length > 0) {
            this.currentVersionIndex = this.versions.length - 1;
            this.showVersionBanner = false;
            this.hasUnsavedChangesInPreviousVersion = false;
            this.pendingVersionContent = "";
            this.lastAutoSaveContent = this.versions[this.currentVersionIndex].content;
            return this.versions[this.currentVersionIndex].content;
        }
        return null;
    }

    setVersionBannerVisible(visible: boolean) {
        this.showVersionBanner = visible;
        if (!visible) {
            this.hasUnsavedChangesInPreviousVersion = false;
            this.pendingVersionContent = "";
        }
    }

    startVersionChecking() {
        if (this.versionCheckInterval) {
            clearInterval(this.versionCheckInterval);
        }
        this.versionCheckInterval = setInterval(() => {
            window.dispatchEvent(new CustomEvent('version:check'));
        }, 10000);
    }

    stopVersionChecking() {
        if (this.versionCheckInterval) {
            clearInterval(this.versionCheckInterval);
            this.versionCheckInterval = null;
        }
    }

    async checkForChanges(currentContent: string) {
        if (this.showVersionBanner) {
            if (currentContent !== this.versions[this.currentVersionIndex].content) {
                this.hasUnsavedChangesInPreviousVersion = true;
                this.pendingVersionContent = currentContent;
            } else {
                this.hasUnsavedChangesInPreviousVersion = false;
                this.pendingVersionContent = "";
            }
            return;
        }

        if (currentContent !== this.lastAutoSaveContent && currentContent.trim() !== '') {
            await this.addVersion(currentContent);
        }
    }

    async saveModifiedPreviousVersionAsLatest(): Promise<string | null> {
        if (this.hasUnsavedChangesInPreviousVersion && this.pendingVersionContent) {
            this.currentVersionIndex = this.versions.length - 1;
            await this.addVersion(this.pendingVersionContent);
            this.showVersionBanner = false;
            this.hasUnsavedChangesInPreviousVersion = false;
            const content = this.pendingVersionContent;
            this.pendingVersionContent = "";
            return content;
        }
        return null;
    }

    async discardChangesInPreviousVersion(): Promise<string | null> {
        if (this.hasUnsavedChangesInPreviousVersion) {
            this.hasUnsavedChangesInPreviousVersion = false;
            const content = this.versions[this.currentVersionIndex].content;
            this.pendingVersionContent = "";
            return content;
        }
        return null;
    }

    setCurrentProjectId(projectId: string | null) {
        this.currentProjectId = projectId;
    }
}

export const uiStore = new UIStore(); 